<template>
  <div>
    <exposure-drilldown
      :reportParameters="drillDownParams"
      :is-read-only="isReadOnly"
      :value="drilldownOpen"
      @input="drilldownOpen = $event"
    />

    <page-toolbar style="height:60px">
      <vonap-toolbar-label label="Position Date">
        <period-control
          style="display: inline"
          @change="periodChange"
          :show-all-year-item="true"
          :show-all-period-item="true"
        >
        </period-control>
        <kendo-dropdownlist
          :value="forecastDate"
          :dataSource="forecastDateDataSource"
          @change="onForecastDateChange"
          @databound="onForecastDateDataBound"
          :auto-bind="false"
          data-value-field="oaDate"
          data-text-field="date"
          style="width:132px"
        ></kendo-dropdownlist>
      </vonap-toolbar-label>
      <vonap-toolbar-label v-show="clientId == 0" label="Analyst">
        <analyst-email v-model="analystEmail" @change="analystEmailChanged" />
      </vonap-toolbar-label>
      <vonap-toolbar-label v-show="clientId == 0" label="Responsible">
        <responsible-email v-model="responsibleEmail" @change="responsibleEmailChanged" />
      </vonap-toolbar-label>
      <vonap-toolbar-label label="Ccy Pair">
        <drop-down-list
          :data-items="ccyPairs"
          data-item-key="ccyPairId"
          text-field="pairCodeDisplay"
          :value="ccyPair"
          @change="changeSelectedCcy"
          style="width: 140px"
        />
      </vonap-toolbar-label>
      <vonap-toolbar-label label="Report Layout">
        <drop-down-list
          :data-items="clients"
          data-item-key="clientId"
          text-field="clientName"
          :value="reportLayout"
          @change="changeReportLayout"
          style="width: 180px"
        />
      </vonap-toolbar-label>
      <vonap-toolbar-label label="Last Process Run" style="width:176px">
        <label style="width:250px;margin-right:14px;font-weight:400;top:2px;position:relative"
          >{{ lastProcessLabel }}
        </label>
      </vonap-toolbar-label>
    </page-toolbar>
    <div ref="masterExposureGrid"></div>
  </div>
</template>

<script>
const $ = kendo.jQuery

import {mapGetters} from 'vuex'
import axios from 'axios'
import {enableCustomExportExcel} from '@/helpers/export-helper'
import {getDataItem} from '@/helpers/kendo-helpers'
import {DropDownList} from '@progress/kendo-vue-dropdowns'
import PageToolbar from '@/components/PageToolbar'
import security from '@/mixins/security'
import VonapToolbarLabel from '@/components/VonapToolbarLabel'
import AnalystEmail from '@/components/AnalystEmail'
import ResponsibleEmail from '@/components/ResponsibleEmail'
import ExposureDrilldown from '@/components/Report/ExposureDrilldown'
import PeriodControl from '@/components/PeriodControl'

export default {
  mixins: [security],
  components: {
    PageToolbar,
    VonapToolbarLabel,
    AnalystEmail,
    ResponsibleEmail,
    ExposureDrilldown,
    DropDownList,
    PeriodControl,
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
  },
  mounted() {
    this.$el.addEventListener('click', this.drilldownClick, false)
  },
  beforeDestroy() {
    this.$el.removeEventListener('click', this.drilldownClick)
    const $container = $(this.$refs.masterExposureGrid)
    let masterExposureGrid = $container.getKendoGrid()
    if (masterExposureGrid != null) {
      masterExposureGrid.destroy()
    }
  },
  data() {
    return {
      startDate: null,
      endDate: null,
      forecastDate: 0,
      forecastDateDataSource: new kendo.data.DataSource({
        transport: {
          read: {
            url: () => '/api/report/masterExposure/getForecastDate',
            data: () => ({
              startDate: (this.startDate || '').slice(0, 10),
              endDate: (this.endDate || '').slice(0, 10),
            }),
            type: 'GET',
          },
        },
      }),
      gridColumns: [],
      responsibleEmail: 'ALL',
      analystEmail: 'ALL',
      ccyPair: null,
      ccyPairs: [],
      reportLayout: {
        clientId: localStorage.getItem('reportLayoutClientId') || 0,
        clientName: localStorage.getItem('reportLayoutClientName') || 'Master',
      },
      clients: [],
      drilldownOpen: false,
      drillDownParams: {
        clientId: 0,
        asOfDate: '2021-08-31',
        fromDate: '2021-09-01',
        toDate: '2023-12-31',
        sectorId: -1,
        divisionId: 0,
        ccyPairId: -1,
        ccyPair: 'USD/CAD',
        foreignCcyIsFx: true,
        optionValue: 0,
        quarterView: false,
        historicalView: false,
        marketId: -1,
        unitId: 0,
        ratio: 1,
        startDate: '2021-09-01',
        endDate: '2021-09-30',
        columnType: 1,
      },
      lastProcessLabel: '',
    }
  },
  methods: {
    refreshGrid() {
      this.createGrid()
    },
    async onForecastDateChange({sender}) {
      this.forecastDate = sender.value()
      if (!this.forecastDate) {
        sender.select(0)
        this.forecastDate = sender.value()
      }
      await this.getLastProcessRun()
      await this.getCcyPairs()
      await this.getReportLayouts()
    },
    async getLastProcessRun() {
      const {data} = await axios.get('/api/report/masterExposure/GetLastProcessRun', {
        params: {
          forecastDate: this.forecastDate,
        },
      })
      this.lastProcessLabel = data
    },
    onForecastDateDataBound({sender}) {
      sender.trigger('change')
    },
    responsibleEmailChanged() {
      this.getCcyPairs()
    },
    analystEmailChanged() {
      this.getCcyPairs()
    },
    async getReportLayouts() {
      const {data: clients} = await axios.get('/api/report/masterExposure/getClients')
      this.clients = clients
    },
    async getCcyPairs() {
      const {data: ccyPairs} = await axios.get('/api/report/masterExposure/getCcyPairs', {
        params: {
          forecastDate: this.forecastDate,
          responsibleEmail: this.responsibleEmail,
          analystEmail: this.analystEmail,
        },
      })
      this.ccyPairs = ccyPairs
      ccyPairs.forEach(pair => {
        const code = `${pair.pairCode} (${pair.foreignCcy})`
        pair.pairCodeDisplay = pair.ccyPairId > 0 ? code : pair.pairCode
      })

      this.ccyPair = ccyPairs[0]
      await this.refreshGrid()
    },
    getDynamicColumns(addDrillDown) {
      let allColumns = []

      this.gridColumns
        .filter(function(column) {
          return column.title !== 'TOTAL'
        })
        .forEach(function(column) {
          let cellFormat = column.isRate ? 'N4' : 'N0'

          let newColumn = {
            field: column.columnName,
            title: column.title,
            width: 150,
            headerAttributes: {style: 'text-align: center; font-size: 12px;'},
            attributes: {style: 'text-align: right'},
            sortable: false,
            template: function(e) {
              let value = e[column.columnName] || 0
              return kendo.toString(value, cellFormat)
            },
          }

          if (addDrillDown) {
            switch (column.typeNumId) {
              case 5:
                newColumn.attributes['class'] = 'hedgeCDrillDown'
                break
              case 6:
                newColumn.attributes['class'] = 'hedgeNCDrillDown'
                break
              case 21:
                newColumn.attributes['class'] = 'adjustmentDrillDown'
                break
            }
          }

          allColumns.push(newColumn)
        })

      allColumns.push({})

      return allColumns
    },
    getGridColumns() {
      let allColumns = [
        {
          field: 'CcyPairCode',
          title: 'CcyPair',
          width: 114,
          attributes: {style: 'text-align: center'},
        },
        {
          field: 'Responsible',
          title: 'Responsible',
          width: 140,
        },
        {
          field: 'ClientName',
          title: 'Client',
          width: 232,
        },
        {
          field: 'TotalActiveOrders',
          title: 'Total Ordre en cours',
          format: '{0:N0}',
          width: 110,
          attributes: {style: 'text-align: right'},
        },
        {
          field: 'FlowTypeId',
          title: 'FlowTypeId',
          hidden: true,
        },
        ...this.getDynamicColumns(),
      ]
      return allColumns
    },
    getGridDetailColumns() {
      let allColumns = [
        {
          field: 'PeriodCode',
          title: 'Period',
          width: 100,
          attributes: {style: 'text-align: left'},
        },
        {
          field: 'FlowTypeId',
          title: 'FlowTypeId',
          hidden: true,
        },
        ...this.getDynamicColumns(true),
      ]
      return allColumns
    },
    getDynamicFields() {
      let fields = {
        flowTypeId: {type: 'number', defaultValue: 0, editable: false},
        clientId: {type: 'number', editable: false},
        clientName: {type: 'string', editable: false},
        CcyPairId: {type: 'number', editable: false},
        CcyPairCode: {type: 'string', editable: false},
        periodCode: {type: 'string', editable: false},
        TotalActiveOrders: {type: 'number', editable: false},
      }

      this.gridColumns.forEach(function(column) {
        fields[column.columnName] = {
          field: column.columnName,
          editable: false,
          defaultValue: 0,
          validation: {required: true},
        }
      })

      return fields
    },
    getDataItem(data) {
      let validColumns = this.gridColumns
      let clientId = data['ClientId']
      let ccyPairId = data['CcyPairId']
      let flowTypeId = data['FlowTypeId']
      let forecastItems = []

      validColumns.forEach(function(column) {
        const value = data[column.columnName] || 0
        const newDataItem = {
          flowTypeId: column.flowTypeId,
          Value: value,
        }
        forecastItems.push(newDataItem)
      })

      let dataItem = {
        clientId: clientId,
        ccyPairId: ccyPairId,
        flowTypeId: flowTypeId,
        forecastItems: forecastItems,
      }

      return dataItem
    },
    createGrid() {
      const vm = this
      let $container = $(vm.$refs.masterExposureGrid)
      let showExcelButton = true
      const url = '/api/report/masterExposure'

      return new Promise(function(gridResolve) {
        axios
          .get(url, {
            params: {
              forecastOaDate: vm.forecastDate,
              responsibleEmail: vm.responsibleEmail,
              analystEmail: vm.analystEmail,
              ccyPairId: vm.ccyPair.ccyPairId,
              reportLayoutClientId: vm.reportLayout.clientId,
            },
          })
          .then(function(result) {
            let response = result.data
            vm.gridColumns = response.columns

            let dataSource = new kendo.data.DataSource({
              transport: {
                read: function(options) {
                  options.success(response)
                },
              },
              schema: {
                data: 'data',
                errors: 'errors',
                model: {
                  id: 'flowTypeId',
                  fields: vm.getDynamicFields(vm.gridColumns),
                },
              },
              batch: true,
            })

            let masterExposureGrid = $container.getKendoGrid()
            if (masterExposureGrid != null) {
              masterExposureGrid.destroy()
            }

            masterExposureGrid = $container
              .empty()
              .kendoGrid({
                navigatable: true,
                dataSource: dataSource,
                hidden: false,
                resizable: false,
                reorderable: false,
                sortable: true,
                scrollable: true,
                height: 'calc(100vh - 170px)',
                columns: vm.getGridColumns(vm.gridColumns),
                detailInit: vm.detailInit,
              })
              .data('kendoGrid')

            masterExposureGrid.trigger('dataBound')
            gridResolve(masterExposureGrid)
            vm.numberOfPeriods = response.data.length

            let gridToolbar = $('.page_toolbar-item')

            if (showExcelButton) {
              $.ajaxSettings.traditional = true
              let params = $.param({
                forecastOaDate: vm.forecastDate,
                responsibleEmail: vm.responsibleEmail,
                analystEmail: vm.analystEmail,
                ccyPairId: vm.ccyPair.ccyPairId,
                reportLayoutClientId: vm.reportLayout.clientId,
              })
              delete $.ajaxSettings.traditional

              $('.page_toolbar-item .exportToExcel').remove()
              let excelUrl = url + '/exportToExcel' + '?' + params
              enableCustomExportExcel(gridToolbar, excelUrl)
            }
          })
      })
    },
    detailInit(e) {
      const vm = this
      const $container = $("<div id='detailGrid'/>")
      const dataItem = e.data
      $container
        .appendTo(e.detailCell)
        .kendoGrid({
          dataSource: {
            type: 'aspnetmvc-ajax',
            serverPaging: true,
            serverFiltering: true,
            serverSorting: true,
            pageSize: 30,
            transport: {
              read: {
                type: 'GET',
                url: '/api/report/masterExposure/readDetailGrid',
                data: () => ({
                  forecastOaDate: vm.forecastDate,
                  clientId: dataItem.ClientId,
                  ccyPairId: dataItem.CcyPairId,
                  responsibleEmail: vm.responsibleEmail,
                  analystEmail: vm.analystEmail,
                  reportLayoutClientId: vm.reportLayout.clientId,
                }),
                dataType: 'json',
              },
            },
            schema: {
              errors: 'errors',
              total: 'total',
              data: 'data',
              model: {
                id: 'flowTypeId',
                fields: vm.getDynamicFields(vm.gridColumns),
              },
            },
          },
          toolbar: [],
          editable: false,
          scrollable: false,
          sortable: false,
          pageable: false,
          columns: vm.getGridDetailColumns(vm.gridColumns),
        })
        .data('kendoGrid')
    },
    drilldownClick({target}) {
      if (!/adjustmentDrillDown|hedge\w+DrillDown/.test(target.className)) return

      const dataItem = getDataItem(target)
      const fromDate = kendo.toString(dataItem.FromDate, 's')
      const toDate = kendo.toString(dataItem.ToDate, 's')
      const startDate = kendo.toString(dataItem.StartDate, 's')
      const endDate = kendo.toString(dataItem.EndDate, 's')
      const columnType = target.className.includes('adjustmentDrillDown')
        ? 3
        : target.className.includes('hedgeCDrillDown')
        ? 1
        : 2

      this.drillDownParams.clientId = dataItem.ClientId
      this.drillDownParams.asOfDate = dataItem.ForecastDate
      this.drillDownParams.fromDate = fromDate
      this.drillDownParams.toDate = toDate
      this.drillDownParams.ccyPairId = dataItem.CcyPairId
      this.drillDownParams.ccyPair = dataItem.CcyPairCode
      ;(this.drillDownParams.foreignCcyIsFx = dataItem.IsFx),
        (this.drillDownParams.startDate = startDate)
      this.drillDownParams.endDate = endDate
      this.drillDownParams.columnType = columnType

      this.drilldownOpen = true
    },
    async changeSelectedCcy({target}) {
      this.ccyPair = target.value
      this.refreshGrid()
    },
    async periodChange(e) {
      this.startDate = e.startDate
      this.endDate = e.endDate
      await this.forecastDateDataSource.read()
    },
    async changeReportLayout({target}) {
      this.reportLayout = target.value
      localStorage.setItem('reportLayoutClientId', this.reportLayout.clientId)
      localStorage.setItem('reportLayoutClientName', this.reportLayout.clientName)
      this.refreshGrid()
    },
  },
}
</script>
<style>
.hedgeCDrillDown,
.hedgeNCDrillDown,
.adjustmentDrillDown {
  text-decoration: underline;
  font-weight: bold !important;
}

.hedgeCDrillDown:hover,
.hedgeNCDrillDown:hover,
.adjustmentDrillDown:hover {
  cursor: pointer;
}
</style>
